import type { z } from 'zod'
import { errorResponseSchema } from '@witty-board/shared'

/**
 * Cliente HTTP mínimo. `credentials: 'include'` para que la cookie httpOnly
 * de sesión viaje en cada petición (CLAUDE.md §5). Las respuestas se
 * validan con los esquemas de `shared/`: es la única fuente de verdad del
 * contrato, así que el frontal no confía a ciegas en lo que llega.
 */

const API_BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? ''
const API_PATH_PREFIX = '/api/v1'

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details: Record<string, unknown> | undefined

  constructor(status: number, code: string, message: string, details?: Record<string, unknown>) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = details
  }
}

export interface ApiRequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE'
  body?: unknown
}

async function rawRequest(path: string, options: ApiRequestOptions): Promise<Response> {
  const response = await fetch(`${API_BASE_URL}${API_PATH_PREFIX}${path}`, {
    method: options.method ?? 'GET',
    credentials: 'include',
    headers: options.body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
  })

  if (!response.ok) {
    const data: unknown = await response.json().catch(() => null)
    const parsed = errorResponseSchema.safeParse(data)
    if (parsed.success) {
      throw new ApiError(
        response.status,
        parsed.data.error.code,
        parsed.data.error.message,
        parsed.data.error.details,
      )
    }
    throw new ApiError(response.status, 'UNKNOWN_ERROR', 'Error de red')
  }

  return response
}

/** Petición con cuerpo de respuesta: se valida con el esquema zod del recurso. */
export async function apiRequest<T>(
  path: string,
  schema: z.ZodType<T>,
  options: ApiRequestOptions = {},
): Promise<T> {
  const response = await rawRequest(path, options)
  const data: unknown = await response.json()
  return schema.parse(data)
}

/** Petición sin cuerpo de respuesta (204), como `POST /auth/logout`. */
export async function apiRequestVoid(path: string, options: ApiRequestOptions = {}): Promise<void> {
  await rawRequest(path, options)
}

/**
 * Subida de fichero (`POST /users/me/avatar`, multipart). Sin `Content-Type`
 * a mano: el navegador fija el `boundary` a partir del `FormData`, y ponerlo
 * nosotros lo rompería.
 */
export async function apiUploadRequest<T>(
  path: string,
  schema: z.ZodType<T>,
  file: File,
): Promise<T> {
  const body = new FormData()
  body.set('avatar', file)

  const response = await fetch(`${API_BASE_URL}${API_PATH_PREFIX}${path}`, {
    method: 'POST',
    credentials: 'include',
    body,
  })

  if (!response.ok) {
    const data: unknown = await response.json().catch(() => null)
    const parsed = errorResponseSchema.safeParse(data)
    if (parsed.success) {
      throw new ApiError(
        response.status,
        parsed.data.error.code,
        parsed.data.error.message,
        parsed.data.error.details,
      )
    }
    throw new ApiError(response.status, 'UNKNOWN_ERROR', 'Error de red')
  }

  const data: unknown = await response.json()
  return schema.parse(data)
}
